import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Briefcase, Car, Shield, Calculator, Phone, Mail, Check, ChevronRight } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { useLanguage, Language } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";

const getCompanyServices = (language: Language) => [
  {
    title: getTranslation("companyServiceRegisterCompany", language),
    description: getTranslation("companyServiceRegisterDesc", language),
    icon: <Briefcase className="h-6 w-6 text-primary" />,
    benefits: language === 'pl' ? [
      "Szybka rejestracja w 24-48h przez system S24",
      "Profesjonalne doradztwo w wyborze formy działalności",
      "Kompleksowa obsługa dokumentacji rejestracyjnej",
      "Przygotowanie umowy spółki i aktu założycielskiego",
      "Wsparcie w otwarciu rachunku bankowego",
      "Pomoc w uzyskaniu numeru NIP i REGON",
      "Doradztwo w zakresie doboru kodów PKD"
    ] : language === 'uk' ? [
      "Швидка реєстрація за 24-48 годин",
      "Вибір найкращої форми діяльності",
      "Комплексне обслуговування документації",
      "Підтримка після реєстрації"
    ] : [
      "Szybka rejestracja w 24-48h",
      "Wybór najlepszej formy działalności",
      "Kompleksowa obsługa dokumentacji",
      "Wsparcie po rejestracji"
    ]
  },
  {
    title: getTranslation("companyServiceKRSChangesTitle", language),
    description: getTranslation("companyServiceKRSDesc", language),
    icon: <Check className="h-6 w-6 text-primary" />,
    benefits: language === 'pl' ? [
      "Kompleksowe przygotowanie dokumentów do KRS",
      "Zmiany w składzie zarządu i rady nadzorczej",
      "Aktualizacja kodów PKD (rodzajów działalności)",
      "Zmiana adresu siedziby firmy i nazwy spółki",
      "Zmiany w kapitale zakładowym i strukturze udziałów",
      "Reprezentacja przed sądem rejestrowym",
      "Monitorowanie statusu wniosku i szybka reakcja"
    ] : language === 'uk' ? [
      "Комплексна підготовка документів",
      "Зміни в складі правління компанії",
      "Оновлення кодів PKD (видів діяльності)",
      "Зміна юридичної адреси та назви",
      "Зміни статутного капіталу",
      "Представництво в суді"
    ] : [
      "Aktualizacja danych spółki",
      "Zmiany w zarządzie",
      "Modyfikacja PKD",
      "Zmiana adresu siedziby"
    ]
  },
  {
    title: getTranslation("companyServiceFinancingTitle", language),
    description: getTranslation("companyServiceFinancingDesc", language),
    icon: <ArrowRight className="h-6 w-6 text-primary" />,
    benefits: language === 'uk' ? [
      "Гранти ЄС та державні дотації",
      "Пільгові кредити для бізнесу",
      "Підготовка бізнес-планів",
      "Фінансові прогнози та аналіз",
      "Супровід проектів фінансування",
      "Звітність перед фінансовими установами"
    ] : [
      "Dotacje unijne i krajowe",
      "Pożyczki i kredyty dla firm",
      "Programy wsparcia dla biznesu",
      "Optymalizacja finansowa"
    ]
  },
  {
    title: getTranslation("companyServiceVirtualAddressTitle", language),
    description: getTranslation("companyServiceVirtualAddressDesc", language),
    icon: <Mail className="h-6 w-6 text-primary" />,
    benefits: language === 'pl' ? [
      "Prestiżowy adres biznesowy w centrum Poznania",
      "Odbiór, skanowanie i przesyłanie korespondencji",
      "Dostęp do osobistego panelu online z archiwum dokumentów",
      "Możliwość rejestracji firmy pod naszym adresem",
      "Ochrona prywatności i poufności danych",
      "Profesjonalny wizerunek firmy bez kosztów najmu biura",
      "Powiadomienia o ważnych przesyłkach urzędowych"
    ] : language === 'uk' ? [
      "Престижна адреса в діловому центрі",
      "Отримання та сканування кореспонденції",
      "Особистий онлайн-кабінет для документів",
      "Використання адреси для реєстрації компанії",
      "Конфіденційність та захист даних",
      "Покращення іміджу вашого бізнесу"
    ] : [
      "Prestiżowy adres w centrum Poznania",
      "Skanowanie i przesyłanie korespondencji",
      "Dostęp online do dokumentów",
      "Profesjonalny wizerunek firmy"
    ]
  }
];

const getAutoServices = (language: Language) => [
  {
    title: getTranslation("autoServiceRegisterEUTitle", language),
    description: getTranslation("autoServiceRegisterEUDesc", language),
    icon: <Car className="h-6 w-6 text-primary" />,
    benefits: [
      "Kompleksowa obsługa rejestracji",
      "Pomoc w przygotowaniu dokumentów",
      "Szybka realizacja",
      "Reprezentacja w urzędach"
    ]
  },
  {
    title: getTranslation("autoServiceRegisterNonEUTitle", language),
    description: getTranslation("autoServiceRegisterNonEUDesc", language),
    icon: <Car className="h-6 w-6 text-primary" />,
    benefits: [
      "Obsługa procedur celnych",
      "Uzyskanie wszystkich zezwoleń",
      "Pomoc w opłatach akcyzowych",
      "Rejestracja pojazdów spoza UE"
    ]
  },
  {
    title: getTranslation("autoServiceInsuranceTitle", language),
    description: getTranslation("autoServiceInsuranceDesc", language),
    icon: <Shield className="h-6 w-6 text-primary" />,
    benefits: [
      "Ubezpieczenia OC i AC",
      "Ubezpieczenia NNW",
      "Assistance drogowe",
      "Konkurencyjne stawki"
    ]
  },
  {
    title: getTranslation("autoServiceTechnicalTitle", language),
    description: getTranslation("autoServiceTechnicalDesc", language),
    icon: <Check className="h-6 w-6 text-primary" />,
    benefits: [
      "Pomoc w badaniach technicznych",
      "Wyrejestrowanie pojazdu",
      "Uzyskanie zaświadczeń",
      "Kompleksowa obsługa"
    ]
  }
];

const getSupportServices = (language: Language) => [
  {
    title: getTranslation("supportServiceLegalizationTitle", language),
    description: getTranslation("supportServiceLegalizationDesc", language),
    icon: <Shield className="h-6 w-6 text-primary" />,
    benefits: language === 'pl' ? [
      "Karty pobytu czasowego i stałego",
      "Wizy i zezwolenia na pracę",
      "Przygotowanie wszystkich dokumentów",
      "Doradztwo w wyborze procedury",
      "Wsparcie w całej procedurze",
      "Indywidualne podejście",
      "Reprezentacja przed urzędami",
      "Pomoc w uzyskaniu zameldowania"
    ] : [
      "Karty pobytu czasowego i stałego",
      "Wizy i zezwolenia na pracę",
      "Wsparcie w całej procedurze",
      "Indywidualne podejście"
    ]
  },
  {
    title: getTranslation("supportServicePESELTitle", language),
    description: getTranslation("supportServicePESELDesc", language),
    icon: <Check className="h-6 w-6 text-primary" />,
    benefits: language === 'pl' ? [
      "Szybkie uzyskanie numeru PESEL",
      "Pomoc w wypełnieniu dokumentów",
      "Reprezentacja w urzędach",
      "Doradztwo w zakresie wymaganych dokumentów",
      "Uzyskanie PESEL bez meldunku",
      "Pomoc w założeniu profilu zaufanego",
      "Kompleksowa obsługa",
      "Oszczędność czasu i formalności"
    ] : [
      "Szybkie uzyskanie numeru PESEL",
      "Pomoc w wypełnieniu dokumentów",
      "Reprezentacja w urzędach",
      "Kompleksowa obsługa"
    ]
  },
  {
    title: getTranslation("supportServiceRepresentationTitle", language),
    description: getTranslation("supportServiceRepresentationDesc", language),
    icon: <Briefcase className="h-6 w-6 text-primary" />,
    benefits: language === 'pl' ? [
      "Profesjonalna reprezentacja",
      "Oszczędność czasu",
      "Znajomość procedur i urzędów",
      "Doświadczenie w trudnych sprawach",
      "Reprezentacja przed urzędem wojewódzkim",
      "Reprezentacja przed ZUS i US",
      "Pomoc w wypełnianiu wniosków",
      "Skuteczne działanie"
    ] : [
      "Profesjonalna reprezentacja",
      "Oszczędność czasu",
      "Znajomość procedur",
      "Skuteczne działanie"
    ]
  },
  {
    title: getTranslation("supportServiceTranslationTitle", language),
    description: getTranslation("supportServiceTranslationDesc", language),
    icon: <Mail className="h-6 w-6 text-primary" />,
    benefits: [
      "Tłumaczenia przysięgłe dokumentów",
      "Szeroki zakres języków",
      "Szybka realizacja",
      "Certyfikowane tłumaczenia"
    ]
  }
];

const getTaxServices = (language: Language) => [
  {
    title: getTranslation("taxServiceBasicTitle", language),
    description: getTranslation("taxServiceBasicDesc", language),
    icon: <Calculator className="h-6 w-6 text-primary" />,
    benefits: [
      "Prowadzenie KPiR lub ryczałtu",
      "Deklaracje podatkowe",
      "Deklaracje ZUS",
      "Do 50 dokumentów miesięcznie"
    ]
  },
  {
    title: getTranslation("taxServiceStandardTitle", language),
    description: getTranslation("taxServiceStandardDesc", language),
    icon: <Calculator className="h-6 w-6 text-primary" />,
    benefits: [
      "Pełna księgowość",
      "Wszystkie deklaracje",
      "Dedykowany księgowy",
      "Do 100 dokumentów miesięcznie"
    ]
  },
  {
    title: getTranslation("taxServicePremiumTitle", language),
    description: getTranslation("taxServicePremiumDesc", language),
    icon: <Calculator className="h-6 w-6 text-primary" />,
    benefits: [
      "Nieograniczona liczba dokumentów",
      "Kompleksowe doradztwo podatkowe",
      "Kadry i płace do 10 pracowników",
      "Priorytetowa obsługa"
    ]
  },
  {
    title: getTranslation("taxServicePITTitle", language),
    description: getTranslation("taxServicePITDesc", language),
    icon: <Check className="h-6 w-6 text-primary" />,
    benefits: [
      "Roczne rozliczenie PIT",
      "Maksymalizacja ulg podatkowych",
      "Optymalizacja rozliczenia",
      "Doradztwo podatkowe"
    ]
  }
];

// Service category images
const serviceImages = {
  company: "https://storage.googleapis.com/fenado-ai-farm-public/generated/cbb5bdc8-1d0a-4b39-a027-87b3dd77f051.webp",
  auto: "https://storage.googleapis.com/fenado-ai-farm-public/generated/d394bcbe-6cc5-49ac-8ef5-9350c3c1ffe3.webp",
  support: "https://storage.googleapis.com/fenado-ai-farm-public/generated/1bbdfe93-7ffb-4782-b258-a8799c6e7c6d.webp",
  tax: "https://storage.googleapis.com/fenado-ai-farm-public/generated/8080f412-b273-40fd-9f01-7c032da4651a.webp"
};

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState("company");
  const [activeService, setActiveService] = useState(0);
  const [businessServiceTab, setBusinessServiceTab] = useState("registration");
  const [autoServiceTab, setAutoServiceTab] = useState("eu-registration");
  const [legalizationServiceTab, setLegalizationServiceTab] = useState("legalization");
  const [taxServiceTypeTab, setTaxServiceTypeTab] = useState("accounting");
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    // Check if there's a hash in the URL and set the active tab accordingly
    const hash = location.hash.replace('#', '');
    if (hash && ['company', 'auto', 'support', 'tax'].includes(hash)) {
      setActiveTab(hash);
      setActiveService(0); // Reset service selection when changing tabs
    }
  }, [location]);
  
  // Handle business service tab changes
  const handleBusinessServiceChange = (tab: string) => {
    setBusinessServiceTab(tab);
    switch(tab) {
      case "registration":
        setActiveService(0);
        break;
      case "krs":
        setActiveService(1);
        break;
      case "address":
        setActiveService(3);
        break;
      default:
        setActiveService(0);
    }
  };

  // Handle auto service tab changes
  const handleAutoServiceChange = (tab: string) => {
    setAutoServiceTab(tab);
    switch(tab) {
      case "eu-registration":
        setActiveService(0);
        break;
      case "non-eu-registration":
        setActiveService(1);
        break;
      case "insurance":
        setActiveService(2);
        break;
      default:
        setActiveService(0);
    }
  };
  
  // Handle legalization service tab changes
  const handleLegalizationServiceChange = (tab: string) => {
    setLegalizationServiceTab(tab);
    switch(tab) {
      case "legalization":
        setActiveService(0);
        break;
      case "pesel":
        setActiveService(1);
        break;
      case "representation":
        setActiveService(2);
        break;
      default:
        setActiveService(0);
    }
  };
  
  // Handle tax service type tab changes
  const handleTaxServiceTypeChange = (tab: string) => {
    setTaxServiceTypeTab(tab);
    // Keep the same active service index (e.g., if viewing BASIC package in accounting, show BASIC in tax)
    // This creates a smoother transition between tax and accounting tabs
    // The active service index is maintained (0 for BASIC, 1 for STANDARD, 2 for PREMIUM)
    // activeService remains unchanged to preserve the selected package when switching between Księgowość and Tax
  };

  const companyServices = getCompanyServices(language);
  const autoServices = getAutoServices(language);
  const supportServices = getSupportServices(language);
  const taxServices = getTaxServices(language);

  // Function to get the current services array based on active tab
  const getCurrentServices = () => {
    switch (activeTab) {
      case "company":
        return companyServices;
      case "auto":
        return autoServices;
      case "support":
        return supportServices;
      case "tax":
        return taxServices;
      default:
        return companyServices;
    }
  };

  // Get the current service based on the active tab and active service index
  const currentServices = getCurrentServices();
  const currentService = currentServices[activeService];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header with Background */}
      <div 
        className="relative bg-cover bg-center py-16 text-white" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(https://storage.googleapis.com/fenado-ai-farm-public/generated/0cba5f43-a69c-43c6-b04a-3a9541fb2994.webp)`,
          backgroundPosition: 'center',
        }}
      >
        <div className="container relative z-10">
          <BackButton className="mb-4 text-white hover:text-white/80" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            {getTranslation("servicesPageTitle", language)}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl">
            {getTranslation("servicesPageDescription", language)}
          </p>
        </div>
      </div>
      
      {/* Services Content */}
      <div className="container py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Service Category Tabs - Redesigned with Icons */}
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-3 p-2 mb-8">
            <TabsTrigger 
              value="company" 
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Briefcase className="h-5 w-5" />
              <span>{getTranslation("company", language)}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="auto" 
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Car className="h-5 w-5" />
              <span>{getTranslation("auto", language)}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="support" 
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Shield className="h-5 w-5" />
              <span>{getTranslation("support", language)}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="tax" 
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Calculator className="h-5 w-5" />
              <span>{getTranslation("accounting", language)}</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="mt-8">
            {/* Company Services Tab Content */}
            <TabsContent value="company" id="company">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Side - Category Image and Description */}
                <div className="md:col-span-1">
                  <div className="rounded-lg overflow-hidden shadow-lg mb-6">
                    <img 
                      src={serviceImages.company} 
                      alt="Business Services" 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Briefcase className="h-6 w-6 text-primary" />
                    {getTranslation("servicesCompanyTabTitle", language)}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Oferujemy kompleksowe wsparcie w zakresie zakładania i prowadzenia firm. Nasz zespół ekspertów zadba o wszystkie formalności, abyś mógł skupić się na rozwijaniu swojego biznesu.
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Dlaczego warto skorzystać z naszych usług?</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Oszczędność czasu i formalności</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Profesjonalne doradztwo</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Kompleksowa obsługa</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Wsparcie na każdym etapie</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                {/* Right Side - Service Details */}
                <div className="md:col-span-2">
                  {/* Service Selection Tabs */}
                  <div className="mb-8">
                    <Tabs 
                      value={businessServiceTab} 
                      onValueChange={handleBusinessServiceChange} 
                      className="w-full"
                    >
                      <TabsList className="flex flex-wrap w-full border rounded-lg p-1 bg-muted/20">
                        <TabsTrigger
                          value="registration"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Rejestracja spółek' : 
                           language === 'uk' ? 'Реєстрація компаній' : 
                           language === 'ru' ? 'Регистрация компаний' : 'Company registration'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="krs"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Zmiany w KRS' : 
                           language === 'uk' ? 'ЗМІНИ' : 
                           language === 'ru' ? 'Изменения в KRS' : 'KRS Changes'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="address"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Adres wirtualny' : 
                           language === 'uk' ? 'ВІРТУАЛЬНА АДРЕСА' : 
                           language === 'ru' ? 'Виртуальный адрес' : 'Virtual Address'}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="registration">
                        <div className="pt-4">
                          {/* Content for registration tab */}
                        </div>
                      </TabsContent>
                      <TabsContent value="krs">
                        <div className="pt-4">
                          {/* Content for krs tab */}
                        </div>
                      </TabsContent>
                      <TabsContent value="address">
                        <div className="pt-4">
                          {/* Content for address tab */}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>

                  {/* Service Details Card */}
                  <Card className="border-t-3 border-t-primary shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardHeader className="bg-muted/20 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {currentService.icon}
                          <CardTitle>{currentService.title}</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <CardDescription className="text-base mb-6">{currentService.description}</CardDescription>
                      
                      {/* Benefits List */}
                      <div className="mt-4">
                        <h4 className="font-medium text-lg mb-3">Co zyskujesz:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {currentService.benefits.map((benefit, index) => (
                            <div 
                              key={index} 
                              className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                            >
                              <ChevronRight className="h-5 w-5 text-primary" />
                              <span>{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3 border-t p-6 bg-muted/10">
                      <Button 
                        variant="default" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </TabsContent>
            
            {/* Auto Services Tab Content */}
            <TabsContent value="auto" id="auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Side - Category Image and Description */}
                <div className="md:col-span-1">
                  <div className="rounded-lg overflow-hidden shadow-lg mb-6">
                    <img 
                      src={serviceImages.auto} 
                      alt="Automotive Services" 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Car className="h-6 w-6 text-primary" />
                    {getTranslation("servicesAutoTabTitle", language)}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Kompleksowa obsługa w zakresie rejestracji i ubezpieczenia pojazdów z Polski i zagranicy. Oszczędzamy Twój czas i ograniczamy formalności do minimum.
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Dlaczego warto skorzystać z naszych usług?</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Oszczędność czasu i formalności</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Profesjonalne doradztwo</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Kompleksowa obsługa</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Konkurencyjne ceny</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                {/* Right Side - Service Details */}
                <div className="md:col-span-2">
                  {/* Service Selection Tabs */}
                  <div className="mb-8">
                    <Tabs 
                      value={autoServiceTab} 
                      onValueChange={handleAutoServiceChange} 
                      className="w-full"
                    >
                      <TabsList className="flex flex-wrap w-full border rounded-lg p-1 bg-muted/20">
                        <TabsTrigger
                          value="eu-registration"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Rejestracja aut z UE' : 
                           language === 'uk' ? 'Реєстрація авто з ЄС' : 
                           language === 'ru' ? 'Регистрация авто из ЕС' : 'EU Car Registration'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="non-eu-registration"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Rejestracja aut spoza UE' : 
                           language === 'uk' ? 'Реєстрація авто з-поза меж ЄС' : 
                           language === 'ru' ? 'Регистрация авто из-за пределов ЕС' : 'Non-EU Car Registration'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="insurance"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Ubezpieczenia' : 
                           language === 'uk' ? 'Страхування' : 
                           language === 'ru' ? 'Страхование' : 'Insurance'}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="eu-registration">
                        <div className="pt-4">
                          {/* Content for EU registration tab */}
                        </div>
                      </TabsContent>
                      <TabsContent value="non-eu-registration">
                        <div className="pt-4">
                          {/* Content for non-EU registration tab */}
                        </div>
                      </TabsContent>
                      <TabsContent value="insurance">
                        <div className="pt-4">
                          {/* Content for insurance tab */}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>

                  {/* Service Details Card */}
                  <Card className="border-t-3 border-t-primary shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardHeader className="bg-muted/20 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {autoServices[activeService].icon}
                          <CardTitle>{autoServices[activeService].title}</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <CardDescription className="text-base mb-6">{autoServices[activeService].description}</CardDescription>
                      
                      {/* Benefits List */}
                      <div className="mt-4">
                        <h4 className="font-medium text-lg mb-3">Co zyskujesz:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {autoServices[activeService].benefits.map((benefit, index) => (
                            <div 
                              key={index} 
                              className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                            >
                              <ChevronRight className="h-5 w-5 text-primary" />
                              <span>{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3 border-t p-6 bg-muted/10">
                      <Button 
                        variant="default" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </TabsContent>
            
            {/* Support Services Tab Content */}
            <TabsContent value="support" id="support">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Side - Category Image and Description */}
                <div className="md:col-span-1">
                  <div className="rounded-lg overflow-hidden shadow-lg mb-6">
                    <img 
                      src={serviceImages.support} 
                      alt="Legal Support Services" 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Shield className="h-6 w-6 text-primary" />
                    {getTranslation("servicesSupportTabTitle", language)}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Pomagamy w sprawach związanych z legalizacją pobytu cudzoziemców w Polsce. Oferujemy wsparcie w uzyskaniu niezbędnych dokumentów i reprezentację przed urzędami.
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Dlaczego warto skorzystać z naszych usług?</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Znajomość procedur i przepisów</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Oszczędność czasu</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Profesjonalne doradztwo</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Indywidualne podejście</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                {/* Right Side - Service Details */}
                <div className="md:col-span-2">
                  {/* Service Selection Tabs */}
                  <div className="mb-8">
                    <Tabs 
                      value={legalizationServiceTab} 
                      onValueChange={handleLegalizationServiceChange} 
                      className="w-full"
                    >
                      <TabsList className="flex flex-wrap w-full border rounded-lg p-1 bg-muted/20">
                        <TabsTrigger
                          value="legalization"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Legalizacja pobytu' : 
                           language === 'uk' ? 'Легалізація перебування' : 
                           language === 'ru' ? 'Легализация пребывания' : 'Residence permit'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="pesel"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Uzyskanie PESEL' : 
                           language === 'uk' ? 'Отримання PESEL' : 
                           language === 'ru' ? 'Получение PESEL' : 'Obtaining PESEL'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="representation"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Reprezentacja przed urzędami' : 
                           language === 'uk' ? 'Представництво перед органами' : 
                           language === 'ru' ? 'Представительство перед органами' : 'Office Representation'}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="legalization">
                        <div className="pt-4">
                          {/* Content for legalization tab */}
                        </div>
                      </TabsContent>
                      <TabsContent value="pesel">
                        <div className="pt-4">
                          {/* Content for pesel tab */}
                        </div>
                      </TabsContent>
                      <TabsContent value="representation">
                        <div className="pt-4">
                          {/* Content for representation tab */}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>

                  {/* Service Details Card */}
                  <Card className="border-t-3 border-t-primary shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardHeader className="bg-muted/20 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {supportServices[activeService].icon}
                          <CardTitle>{supportServices[activeService].title}</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <CardDescription className="text-base mb-6">{supportServices[activeService].description}</CardDescription>
                      
                      {/* Benefits List */}
                      <div className="mt-4">
                        <h4 className="font-medium text-lg mb-3">Co zyskujesz:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {supportServices[activeService].benefits.map((benefit, index) => (
                            <div 
                              key={index} 
                              className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                            >
                              <ChevronRight className="h-5 w-5 text-primary" />
                              <span>{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3 border-t p-6 bg-muted/10">
                      <Button 
                        variant="default" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </TabsContent>
            
            {/* Tax Services Tab Content */}
            <TabsContent value="tax" id="tax">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Side - Category Image and Description */}
                <div className="md:col-span-1">
                  <div className="rounded-lg overflow-hidden shadow-lg mb-6">
                    <img 
                      src={serviceImages.tax} 
                      alt="Accounting Services" 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Calculator className="h-6 w-6 text-primary" />
                    {taxServiceTypeTab === "accounting" 
                      ? getTranslation("servicesTaxTabTitle", language)
                      : language === 'pl' ? "Usługi podatkowe" : 
                        language === 'uk' ? "Податкові послуги" :
                        language === 'ru' ? "Налоговые услуги" : "Tax Services"}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {taxServiceTypeTab === "accounting"
                      ? language === 'pl'
                        ? "Zapewniamy profesjonalną obsługę księgową dopasowaną do potrzeb Twojej firmy. Oferujemy różne pakiety usług księgowych, aby każdy znalazł rozwiązanie idealne dla siebie."
                        : language === 'uk'
                          ? "Ми надаємо професійні бухгалтерські послуги, адаптовані до потреб вашої компанії. Пропонуємо різні пакети бухгалтерських послуг, щоб кожен знайшов ідеальне рішення для себе."
                          : language === 'ru'
                            ? "Мы предоставляем профессиональные бухгалтерские услуги, адаптированные к потребностям вашей компании. Предлагаем различные пакеты бухгалтерских услуг, чтобы каждый нашел идеальное решение для себя."
                            : "We provide professional accounting services tailored to the needs of your company. We offer various accounting service packages so that everyone can find the perfect solution for themselves."
                      : language === 'pl'
                        ? "Zapewniamy profesjonalne usługi doradztwa podatkowego dopasowane do potrzeb Twojej firmy. Nasze pakiety usług podatkowych pomogą Ci zoptymalizować obciążenia fiskalne, zaplanować strategię podatkową i zapewnić zgodność z przepisami."
                        : language === 'uk'
                          ? "Ми надаємо професійні послуги податкового консультування, адаптовані до потреб вашої компанії. Наші пакети податкових послуг допоможуть вам оптимізувати податкові навантаження, спланувати податкову стратегію та забезпечити відповідність нормативним вимогам."
                          : language === 'ru'
                            ? "Мы предоставляем профессиональные услуги налогового консультирования, адаптированные к потребностям вашей компании. Наши пакеты налоговых услуг помогут вам оптимизировать налоговые нагрузки, спланировать налоговую стратегию и обеспечить соответствие нормативным требованиям."
                            : "We provide professional tax advisory services tailored to the needs of your company. Our tax service packages will help you optimize fiscal burdens, plan your tax strategy, and ensure compliance with regulations."}
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Dlaczego warto skorzystać z naszych usług?</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Doświadczeni księgowi</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Indywidualne podejście</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Elastyczne pakiety</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Doradztwo podatkowe</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                {/* Right Side - Service Details */}
                <div className="md:col-span-2">
                  {/* Tax/Accounting Service Type Selection */}
                  <div className="mb-4">
                    <Tabs 
                      value={taxServiceTypeTab} 
                      onValueChange={handleTaxServiceTypeChange} 
                      className="w-full"
                    >
                      <TabsList className="flex flex-wrap w-full border rounded-lg p-1 bg-muted/20">
                        <TabsTrigger
                          value="accounting"
                          className="flex-1 py-3"
                        >
                          {getTranslation("accounting", language)}
                        </TabsTrigger>
                        <TabsTrigger
                          value="tax"
                          className="flex-1 py-3"
                        >
                          {getTranslation("tax", language)}
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  
                  {/* Accounting Packages Selection */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-3">
                      {taxServiceTypeTab === "accounting" ?
                        language === 'pl' ? 'Księgowość - Zapewniamy profesjonalną obsługę księgową dopasowaną do potrzeb Twojej firmy. Oferujemy różne pakiety usług księgowych.' : 
                        language === 'uk' ? 'Księgowoś Zapewniamy profesjonalną obsługę księgową dopasowaną do potrzeb Twojej firmy. Oferujemy różne pakiety usług księgowych.' :
                        language === 'ru' ? 'Бухгалтерия - Мы предоставляем профессиональные бухгалтерские услуги, адаптированные к потребностям вашей компании. Предлагаем различные пакеты бухгалтерских услуг.' :
                        'Accounting - We provide professional accounting services tailored to the needs of your company. We offer various accounting service packages.'
                      :
                        language === 'pl' ? 'Tax - Zapewniamy profesjonalną obsługę podatkową dopasowaną do potrzeb Twojej firmy. Oferujemy różne pakiety usług podatkowych.' : 
                        language === 'uk' ? 'Tax - Ми надаємо професійні податкові послуги, адаптовані до потреб вашої компанії. Пропонуємо різні пакети податкових послуг.' :
                        language === 'ru' ? 'Tax - Мы предоставляем профессиональные налоговые услуги, адаптированные к потребностям вашей компании. Предлагаем различные пакеты налоговых услуг.' :
                        'Tax - We provide professional tax services tailored to the needs of your company. We offer various tax service packages.'
                      }
                    </h3>
                    <Tabs
                      value={activeService.toString()}
                      onValueChange={(value) => setActiveService(parseInt(value))}
                      className="w-full"
                    >
                      <TabsList className="flex flex-wrap w-full border rounded-lg p-1 bg-muted/20">
                        <TabsTrigger
                          value="0"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Pakiet podstawowy' : 
                          language === 'uk' ? 'Pakiet podstawowy' : 
                          language === 'ru' ? 'Базовый пакет' : 
                          'Basic package'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="1"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Pakiet standard' : 
                          language === 'uk' ? 'Pakiet standard' : 
                          language === 'ru' ? 'Стандартный пакет' : 
                          'Standard package'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="2"
                          className="flex-1 py-3"
                        >
                          {language === 'pl' ? 'Pakiet premium' : 
                          language === 'uk' ? 'Pakiet premium' : 
                          language === 'ru' ? 'Премиум пакет' : 
                          'Premium package'}
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>

                  {/* Service Details Card */}
                  <Card className="border-t-3 border-t-primary shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardHeader className="bg-muted/20 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {taxServices[activeService].icon}
                          <CardTitle>
                            {taxServiceTypeTab === "accounting" 
                              ? (activeService === 0 
                                ? language === 'pl' ? "Księgowość - Pakiet podstawowy" 
                                : language === 'uk' ? "Księgowoś - Pakiet podstawowy" 
                                : language === 'ru' ? "Бухгалтерия - Базовый пакет" 
                                : "Accounting - Basic package"
                              : activeService === 1
                                ? language === 'pl' ? "Księgowość - Pakiet standard" 
                                : language === 'uk' ? "Księgowoś - Pakiet standard" 
                                : language === 'ru' ? "Бухгалтерия - Стандартный пакет" 
                                : "Accounting - Standard package"
                              : language === 'pl' ? "Księgowość - Pakiet premium" 
                                : language === 'uk' ? "Księgowoś - Pakiet premium" 
                                : language === 'ru' ? "Бухгалтерия - Премиум пакет" 
                                : "Accounting - Premium package")
                              : (activeService === 0 
                                ? language === 'pl' ? "Tax - Pakiet podstawowy" 
                                : language === 'uk' ? "Tax - Базовий пакет" 
                                : language === 'ru' ? "Tax - Базовый пакет" 
                                : "Tax - Basic package"
                              : activeService === 1
                                ? language === 'pl' ? "Tax - Pakiet standard" 
                                : language === 'uk' ? "Tax - Стандартний пакет" 
                                : language === 'ru' ? "Tax - Стандартный пакет" 
                                : "Tax - Standard package"
                              : language === 'pl' ? "Tax - Pakiet premium" 
                                : language === 'uk' ? "Tax - Преміум пакет" 
                                : language === 'ru' ? "Tax - Премиум пакет" 
                                : "Tax - Premium package")
                            }
                          </CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <CardDescription className="text-base mb-6">
                        {taxServiceTypeTab === "accounting" 
                          ? activeService === 0
                            ? language === 'pl' 
                              ? "Podstawowa obsługa księgowa dla małych firm i jednoosobowych działalności gospodarczych. Obejmuje prowadzenie KPiR lub ryczałtu, deklaracje podatkowe i ZUS, do 50 dokumentów miesięcznie."
                              : language === 'uk'
                                ? "Podstawowa obsługa księgowa dla małych firm i jednoosobowych działalności gospodarczych. Obejmuje prowadzenie KPiR lub ryczałtu, deklaracje podatkowe i ZUS, do 50 dokumentów miesięcznie."
                                : language === 'ru'
                                  ? "Базовое бухгалтерское обслуживание для малых предприятий и индивидуальных предпринимателей. Включает ведение KPiR или единого налога, налоговые декларации и ZUS, до 50 документов в месяц."
                                  : "Basic accounting service for small businesses and sole proprietorships. Includes KPiR or flat-rate accounting, tax and ZUS declarations, up to 50 documents per month."
                            : activeService === 1
                              ? language === 'pl'
                                ? "Kompleksowa obsługa księgowa dla rozwijających się firm. Obejmuje pełną księgowość, deklaracje podatkowe, ZUS, podstawowe doradztwo oraz dedykowanego księgowego. Do 100 dokumentów miesięcznie."
                                : language === 'uk'
                                  ? "Pełna księgowość, Do 100 dokumentów miesięcznie, Deklaracje podatkowe, Deklaracje ZUS, Podstawowe doradztwo podatkowe, Dedykowany księgowy"
                                  : language === 'ru'
                                    ? "Комплексное бухгалтерское обслуживание для развивающихся компаний. Включает полную бухгалтерию, налоговые декларации, ZUS, базовые консультации и выделенного бухгалтера. До 100 документов в месяц."
                                    : "Comprehensive accounting service for developing companies. Includes full accounting, tax declarations, ZUS, basic advisory, and a dedicated accountant. Up to 100 documents per month."
                              : language === 'pl'
                                ? "Pełna obsługa księgowa i doradztwo podatkowe dla średnich i dużych firm. Obejmuje pełną księgowość, nieograniczoną liczbę dokumentów, wszystkie deklaracje, kompleksowe doradztwo podatkowe, kadry i płace do 10 pracowników, priorytetową obsługę oraz dedykowanego głównego księgowego."
                                : language === 'uk'
                                  ? "Pełna księgowość, Nieograniczona liczba dokumentów, Wszystkie deklaracje, Kadry i płace do 10 pracowników, Kompleksowe doradztwo podatkowe, Priorytetowa obsługa, Dedykowany główny księgowy"
                                  : language === 'ru'
                                    ? "Полное бухгалтерское обслуживание и налоговое консультирование для средних и крупных компаний. Включает полную бухгалтерию, неограниченное количество документов, все декларации, комплексные налоговые консультации, кадры и зарплату для до 10 работников и приоритетное обслуживание с главным бухгалтером."
                                    : "Full accounting service and tax advisory for medium and large companies. Includes full accounting, unlimited documents, all declarations, comprehensive tax advisory, HR and payroll for up to 10 employees, and priority service with a chief accountant."
                          : activeService === 0
                            ? language === 'pl' 
                              ? "Zapewniamy profesjonalne usługi podatkowe dla małych firm i jednoosobowych działalności gospodarczych. Pakiet TAX PODSTAWOWY obejmuje optymalizację podatkową, analizę obciążeń fiskalnych oraz podstawowe doradztwo w zakresie podatku VAT i PIT."
                              : language === 'uk'
                                ? "Ми надаємо професійні податкові послуги для малих підприємств та індивідуальних підприємців. Пакет TAX БАЗОВИЙ включає податкову оптимізацію, аналіз податкових навантажень та базові консультації щодо ПДВ і податку на доходи."
                                : language === 'ru'
                                  ? "Мы предоставляем профессиональные налоговые услуги для малых предприятий и индивидуальных предпринимателей. Пакет TAX БАЗОВЫЙ включает налоговую оптимизацию, анализ налоговых нагрузок и базовые консультации по НДС и подоходному налогу."
                                  : "We provide professional tax services for small businesses and sole traders. The TAX BASIC package includes tax optimization, analysis of fiscal burdens, and basic VAT and PIT advisory."
                            : activeService === 1
                              ? language === 'pl'
                                ? "Kompleksowe usługi podatkowe dla rozwijających się firm. Pakiet TAX STANDARD zawiera pełne doradztwo podatkowe, optymalizację kosztów podatkowych oraz strategiczne planowanie podatkowe dla Twojej firmy."
                                : language === 'uk'
                                  ? "Комплексні податкові послуги для компаній, що розвиваються. Пакет TAX СТАНДАРТНИЙ включає повне податкове консультування, оптимізацію податкових витрат та стратегічне податкове планування для вашої компанії."
                                  : language === 'ru'
                                    ? "Комплексные налоговые услуги для развивающихся компаний. Пакет TAX СТАНДАРТНЫЙ включает полное налоговое консультирование, оптимизацию налоговых расходов и стратегическое налоговое планирование для вашей компании."
                                    : "Comprehensive tax services for developing companies. The TAX STANDARD package includes full tax advisory, optimization of tax costs, and strategic tax planning for your company."
                              : language === 'pl'
                                ? "Zaawansowane usługi podatkowe dla średnich i dużych przedsiębiorstw. Pakiet TAX PREMIUM obejmuje kompleksowe doradztwo podatkowe, nieograniczoną liczbę konsultacji, międzynarodowe planowanie podatkowe oraz reprezentację przed organami podatkowymi."
                                : language === 'uk'
                                  ? "Розширені податкові послуги для середніх та великих підприємств. Пакет TAX ПРЕМІУМ включає комплексні податкові консультації, необмежену кількість консультацій, міжнародне податкове планування та представництво перед податковими органами."
                                  : language === 'ru'
                                    ? "Расширенные налоговые услуги для средних и крупных предприятий. Пакет TAX ПРЕМИУМ включает комплексные налоговые консультации, неограниченное количество консультаций, международное налоговое планирование и представительство перед налоговыми органами."
                                    : "Advanced tax services for medium and large enterprises. The TAX PREMIUM package includes comprehensive tax advisory, unlimited consultations, international tax planning, and representation before tax authorities."
                        }
                      </CardDescription>
                      
                      {/* Benefits List */}
                      <div className="mt-4">
                        <h4 className="font-medium text-lg mb-3">{language === 'pl' ? 'Co zyskujesz:' : language === 'uk' ? 'Що ви отримуєте:' : language === 'ru' ? 'Что вы получаете:' : 'What you gain:'}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {taxServiceTypeTab === "accounting" 
                            ? activeService === 0 
                              ? (language === 'pl' ? [
                                  "Prowadzenie KPiR lub ryczałt",
                                  "Do 50 dokumentów miesięcznie",
                                  "Deklaracje podatkowe",
                                  "Deklaracje ZUS",
                                  "Kontakt e-mail i telefoniczny",
                                  "Podstawowe doradztwo"
                                ] : language === 'uk' ? [
                                  "Prowadzenie KPiR lub ryczałt",
                                  "Do 50 dokumentów miesięcznie",
                                  "Deklaracje podatkowe",
                                  "Deklaracje ZUS",
                                  "Kontakt e-mail i telefoniczny",
                                  "Podstawowe doradztwo"
                                ] : language === 'ru' ? [
                                  "Ведение KPiR или единый налог",
                                  "До 50 документов в месяц",
                                  "Налоговые декларации",
                                  "Декларации ZUS",
                                  "Контакт по электронной почте и телефону",
                                  "Базовые консультации"
                                ] : [
                                  "KPiR or flat-rate accounting",
                                  "Up to 50 documents per month",
                                  "Tax declarations",
                                  "ZUS declarations",
                                  "Email and phone contact",
                                  "Basic advisory"
                                ]).map((benefit, index) => (
                                  <div 
                                    key={index} 
                                    className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                                  >
                                    <ChevronRight className="h-5 w-5 text-primary" />
                                    <span>{benefit}</span>
                                  </div>
                                ))
                              : activeService === 1
                                ? (language === 'pl' ? [
                                    "Pełna księgowość",
                                    "Do 100 dokumentów miesięcznie",
                                    "Deklaracje podatkowe",
                                    "Deklaracje ZUS",
                                    "Podstawowe doradztwo podatkowe",
                                    "Dedykowany księgowy"
                                  ] : language === 'uk' ? [
                                    "Pełna księgowość",
                                    "Do 100 dokumentów miesięcznie",
                                    "Deklaracje podatkowe",
                                    "Deklaracje ZUS",
                                    "Podstawowe doradztwo podatkowe",
                                    "Dedykowany księgowy"
                                  ] : language === 'ru' ? [
                                    "Полная бухгалтерия",
                                    "До 100 документов в месяц",
                                    "Налоговые декларации",
                                    "Декларации ZUS",
                                    "Базовое налоговое консультирование",
                                    "Выделенный бухгалтер",
                                    "Контакт по электронной почте и телефону"
                                  ] : [
                                    "Full accounting",
                                    "Up to 100 documents per month",
                                    "Tax declarations",
                                    "ZUS declarations",
                                    "Basic tax advisory",
                                    "Dedicated accountant",
                                    "Email and phone contact"
                                  ]).map((benefit, index) => (
                                    <div 
                                      key={index} 
                                      className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                                    >
                                      <ChevronRight className="h-5 w-5 text-primary" />
                                      <span>{benefit}</span>
                                    </div>
                                  ))
                                : (language === 'pl' ? [
                                    "Pełna księgowość",
                                    "Nieograniczona liczba dokumentów",
                                    "Wszystkie deklaracje",
                                    "Kadry i płace do 10 pracowników",
                                    "Kompleksowe doradztwo podatkowe",
                                    "Priorytetowa obsługa",
                                    "Dedykowany główny księgowy"
                                  ] : language === 'uk' ? [
                                    "Pełna księgowość",
                                    "Nieograniczona liczba dokumentów",
                                    "Wszystkie deklaracje",
                                    "Kadry i płace do 10 pracowników",
                                    "Kompleksowe doradztwo podatkowe",
                                    "Priorytetowa obsługa",
                                    "Dedykowany główny księgowy"
                                  ] : language === 'ru' ? [
                                    "Полная бухгалтерия",
                                    "Неограниченное количество документов",
                                    "Все декларации",
                                    "Кадры и зарплата до 10 работников",
                                    "Комплексные налоговые консультации",
                                    "Приоритетная поддержка",
                                    "Выделенный главный бухгалтер",
                                    "Контакт по электронной почте, телефону и личный",
                                    "Поддержка во время проверок",
                                    "Налоговая оптимизация"
                                  ] : [
                                    "Full accounting",
                                    "Unlimited number of documents",
                                    "All declarations",
                                    "HR and payroll up to 10 employees",
                                    "Comprehensive tax advisory",
                                    "Priority support",
                                    "Dedicated chief accountant",
                                    "Email, phone, and personal contact",
                                    "Support during inspections",
                                    "Tax optimization"
                                  ]).map((benefit, index) => (
                                    <div 
                                      key={index} 
                                      className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                                    >
                                      <ChevronRight className="h-5 w-5 text-primary" />
                                      <span>{benefit}</span>
                                    </div>
                                  ))
                            : activeService === 0 
                              ? (language === 'pl' ? [
                                  "Optymalizacja podatkowa",
                                  "Analiza obciążeń podatkowych",
                                  "Doradztwo w zakresie VAT",
                                  "Planowanie zobowiązań podatkowych",
                                  "Przegląd dokumentacji pod kątem podatkowym",
                                  "Kwartalny raport podatkowy"
                                ] : language === 'uk' ? [
                                  "Податкова оптимізація",
                                  "Аналіз податкових навантажень",
                                  "Консультації щодо ПДВ",
                                  "Планування податкових зобов'язань",
                                  "Перегляд документації з податкової точки зору",
                                  "Щоквартальний податковий звіт"
                                ] : language === 'ru' ? [
                                  "Налоговая оптимизация",
                                  "Анализ налоговых нагрузок",
                                  "Консультации по НДС",
                                  "Планирование налоговых обязательств",
                                  "Просмотр документации с налоговой точки зрения",
                                  "Ежеквартальный налоговый отчет"
                                ] : [
                                  "Tax optimization",
                                  "Analysis of tax burdens",
                                  "VAT advisory",
                                  "Tax liability planning",
                                  "Document review for tax purposes",
                                  "Quarterly tax report"
                                ]).map((benefit, index) => (
                                  <div 
                                    key={index} 
                                    className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                                  >
                                    <ChevronRight className="h-5 w-5 text-primary" />
                                    <span>{benefit}</span>
                                  </div>
                                ))
                              : activeService === 1 
                                ? (language === 'pl' ? [
                                    "Pełne doradztwo podatkowe",
                                    "Optymalizacja kosztów podatkowych",
                                    "Planowanie podatkowe",
                                    "Opinie podatkowe",
                                    "Analiza umów pod kątem podatkowym",
                                    "Miesięczny raport podatkowy"
                                  ] : language === 'uk' ? [
                                    "Повне податкове консультування",
                                    "Оптимізація податкових витрат",
                                    "Податкове планування",
                                    "Податкові висновки",
                                    "Аналіз договорів з податкової точки зору",
                                    "Щомісячний податковий звіт"
                                  ] : language === 'ru' ? [
                                    "Полное налоговое консультирование",
                                    "Оптимизация налоговых расходов",
                                    "Налоговое планирование",
                                    "Налоговые заключения",
                                    "Анализ договоров с налоговой точки зрения",
                                    "Ежемесячный налоговый отчет"
                                  ] : [
                                    "Full tax advisory",
                                    "Optimization of tax costs",
                                    "Tax planning",
                                    "Tax opinions",
                                    "Contract analysis for tax purposes",
                                    "Monthly tax report"
                                  ]).map((benefit, index) => (
                                    <div 
                                      key={index} 
                                      className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                                    >
                                      <ChevronRight className="h-5 w-5 text-primary" />
                                      <span>{benefit}</span>
                                    </div>
                                  ))
                                : (language === 'pl' ? [
                                    "Kompleksowe doradztwo podatkowe",
                                    "Nieograniczona liczba konsultacji",
                                    "Międzynarodowe planowanie podatkowe",
                                    "Reprezentacja przed organami podatkowymi",
                                    "Audyt podatkowy",
                                    "Wsparcie podczas kontroli skarbowej",
                                    "Indywidualne interpretacje podatkowe",
                                    "Dedykowany doradca podatkowy"
                                  ] : language === 'uk' ? [
                                    "Комплексні податкові консультації",
                                    "Необмежена кількість консультацій",
                                    "Міжнародне податкове планування",
                                    "Представництво перед податковими органами",
                                    "Податковий аудит",
                                    "Підтримка під час податкових перевірок",
                                    "Індивідуальні податкові інтерпретації",
                                    "Персональний податковий консультант"
                                  ] : language === 'ru' ? [
                                    "Комплексные налоговые консультации",
                                    "Неограниченное количество консультаций",
                                    "Международное налоговое планирование",
                                    "Представительство перед налоговыми органами",
                                    "Налоговый аудит",
                                    "Поддержка во время налоговых проверок",
                                    "Индивидуальные налоговые интерпретации",
                                    "Персональный налоговый консультант"
                                  ] : [
                                    "Comprehensive tax advisory",
                                    "Unlimited consultations",
                                    "International tax planning",
                                    "Representation before tax authorities",
                                    "Tax audit",
                                    "Support during tax inspections",
                                    "Individual tax interpretations",
                                    "Dedicated tax advisor"
                                  ]).map((benefit, index) => (
                                    <div 
                                      key={index} 
                                      className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg"
                                    >
                                      <ChevronRight className="h-5 w-5 text-primary" />
                                      <span>{benefit}</span>
                                    </div>
                                  ))
                          }
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3 border-t p-6 bg-muted/10">
                      <Button 
                        variant="default" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full sm:w-auto"
                        asChild
                      >
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
        
        {/* CTA Section */}
        <div className="mt-16 py-10 px-8 rounded-xl text-center bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/10 shadow-lg">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">{getTranslation("servicesNotFound", language)}</h3>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            {getTranslation("servicesFlexibleOffer", language)}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="flex items-center gap-2 px-8 py-6 text-lg">
              <a href="tel:+48886695593">
                <Phone className="h-5 w-5" />
                {getTranslation("quickContact", language)}
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="flex items-center gap-2 px-8 py-6 text-lg border-2">
              <a href="mailto:kontakt1group.pl@gmail.com">
                <Mail className="h-5 w-5" />
                {getTranslation("emailContact", language)}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}